// src/components/AccountSelector.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaChartLine, FaChevronDown } from "react-icons/fa";

/** Canonical role: settings.CopyTrade.role (+enabled), with safe fallbacks */
const deriveCopyRole = (a) => {
  if (!a) return null;

  // Canonical location: settings.CopyTrade
  const ct = a?.settings?.CopyTrade || a?.CopyTrade || null;

  const enabled = typeof ct?.enabled === "boolean" ? ct.enabled : true;
  const roleLower = String(ct?.role ?? "").trim().toLowerCase();

  // 🚫 Hide if copy trading is disabled
  if (!enabled) return null;

  if (roleLower === "leader") return "leader";
  if (roleLower === "follower") return "follower";

  // Fallbacks for legacy fields
  const candidates = [
    a?.copyTradingTag, a?.copyTraderTag, a?.copyRole, a?.copyTradingRole, a?.role,
    a?.settings?.copyTradingTag, a?.settings?.copyTraderTag,
    a?.settings?.copyRole, a?.settings?.copyTradingRole,
    a?.settings?.role, a?.settings?.tag, a?.tag,
  ];
  for (const c of candidates) {
    const s = String(c ?? "").trim().toLowerCase();
    if (!s) continue;
    if (/\bleader\b/.test(s)) return "leader";
    if (/\bfollower\b/.test(s)) return "follower";
  }

  return null;
};

// Robust “Backtesting” detector across roots & settings
const isBacktesting = (acc) => {
  const s = acc?.settings || {};
  const rootTag = String(acc?.tag || "").toLowerCase();
  const rootFlag = acc?.isBacktest === true;

  const setTag = String(s?.tag || "").toLowerCase();
  const setFlag = s?.isBacktest === true;

  // If you have any ‘fundingType’ field anywhere, check it too:
  const rootFunding = String(acc?.fundingType || "").toLowerCase();
  const setFunding  = String(s?.fundingType || "").toLowerCase();

  return (
    rootFlag || setFlag ||
    rootTag.includes("backtest") || setTag.includes("backtest") ||
    rootFunding.includes("backtest") || setFunding.includes("backtest")
  );
};

// Tiny pill
const backtestPill = (extra = "") => (
  <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold
                    bg-slate-200 text-slate-700 dark:bg-slate-600/40 dark:text-slate-200 ${extra}`}>
    Backtesting
  </span>
);


const getTypeRank = (t) => {
  const s = String(t || "").toLowerCase();
  if (s.includes("future")) return 0;                       // Futures first
  if (s.includes("forex") || s.includes("fx")) return 1;    // then Forex
  return 2;                                                 // anything else last
};
const getRoleRank = (r) => (r === "leader" ? 0 : r === "follower" ? 1 : 2);

const roleBadge = (role, extra = "") => {
  if (!role) return null;
  const isLeader = role === "leader";
  const cls = isLeader
    ? "bg-yellow-500 text-gray-100 dark:bg-yellow-300/90 dark:text-gray-700"
    : "bg-purple-100 text-purple-700 dark:bg-purple-700/40 dark:text-purple-300";
  const label = isLeader ? "Leader" : "Follower";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${cls} ${extra}`}>
      {label}
    </span>
  );
};

// src/components/AccountSelector.jsx

const typePill = (type, extra = "") => {
  if (!type) return null;

  const t = String(type);
  const isFutures = /future/i.test(t);
  const isStocks  = /stock/i.test(t);
  const isMixed   = /mixed/i.test(t);
  const isOptions = /option/i.test(t); // <-- ADD THIS

  const cls = isOptions
    ? "bg-orange-100 text-orange-500 dark:bg-orange-500 dark:text-gray-200" // <-- ADD THIS
    : isStocks
      ? "bg-purple-100 text-purple-700 dark:bg-purple-600/40 dark:text-purple-300"
      : isFutures
        ? "bg-blue-100 text-blue-700 dark:bg-blue-600/40 dark:text-blue-300"
        : isMixed 
          ? "bg-yellow-100 text-gray-400 dark:bg-yellow-500 dark:text-gray-100" 
          : "bg-green-100 text-green-700 dark:bg-green-600/40 dark:text-green-300";

  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${cls} ${extra}`}>
      {type}
    </span>
  );
};

export default function AccountSelector({
  accounts = [],
  selectedAccountId,
  onSelectAccount,
  isPrivacyMode,
  ...rest
}) {
  // ✅ Hooks must be called unconditionally, so they live at the top:
  const sortedAccounts = useMemo(() => {
    if (!Array.isArray(accounts)) return [];
    return [...accounts].sort((a, b) => {
      const typeDiff = getTypeRank(a.type) - getTypeRank(b.type);
      if (typeDiff !== 0) return typeDiff;
      const ra = deriveCopyRole(a);
      const rb = deriveCopyRole(b);
      const roleDiff = getRoleRank(ra) - getRoleRank(rb);
      if (roleDiff !== 0) return roleDiff;
      return String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" });
    });
  }, [accounts]);

  const selectedIndex = useMemo(() => {
    const i = sortedAccounts.findIndex(a => a.id === selectedAccountId);
    return i >= 0 ? i : 0;
  }, [sortedAccounts, selectedAccountId]);

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(selectedIndex);
  const buttonRef = useRef(null);
  const listRef = useRef(null);

  // Keep the highlighted item aligned with the selectedAccountId
  useEffect(() => {
    setActiveIndex(selectedIndex);
  }, [selectedIndex]);

  // Keyboard + outside-click handling (only attaches when open)
  useEffect(() => {
    if (!open) return;

    const onClickOutside = (e) => {
      if (
        buttonRef.current && !buttonRef.current.contains(e.target) &&
        listRef.current && !listRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") { setOpen(false); buttonRef.current?.focus(); }
      else if (e.key === "ArrowDown") { e.preventDefault(); setActiveIndex(i => (i + 1) % sortedAccounts.length); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIndex(i => (i - 1 + sortedAccounts.length) % sortedAccounts.length); }
      else if (e.key === "Enter") {
        e.preventDefault();
        const acc = sortedAccounts[activeIndex];
        if (acc) { onSelectAccount(acc.id); setOpen(false); buttonRef.current?.focus(); }
      }
    };

    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, sortedAccounts, activeIndex, onSelectAccount]);

  // After hooks, we can branch the UI safely
  const hasAccounts = sortedAccounts.length > 0;
  const single = sortedAccounts.length === 1;
  const selected = hasAccounts ? sortedAccounts[selectedIndex] : null;

  if (!hasAccounts) {
    return <div {...rest}>No accounts found.</div>;
  }

  if (single) {
    const acc = sortedAccounts[0];
    const role = deriveCopyRole(acc);
    return (
      <div
        {...rest}
        className="w-full max-w-2xl mx-auto bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 border border-gray-200 dark:border-gray-700 flex flex-col items-center mb-8"
      >
        <div className="mb-1 text-center text-lg font-semibold text-gray-800 dark:text-gray-100">
          <span>Account:</span>
        </div>
        <div className="font-bold text-blue-700 dark:text-blue-400 text-xl mb-2 flex items-center gap-2 flex-wrap justify-center">
          {acc.name}
          {typePill(acc.type)}
          {isBacktesting(acc)}
          {roleBadge(role)}
        </div>
      </div>
    );
  }

  // Multi-account: custom button + listbox with colored tags
  return (
    <div
      {...rest}
      className="w-full max-w-2xl mx-auto bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 border border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center gap-8 mb-8"
    >
      <div className="flex-shrink-0 flex flex-col items-center gap-2">
        <div className="w-14 h-14 flex items-center justify-center rounded-full bg-gradient-to-br from-blue-200 to-blue-500 dark:from-blue-700 dark:to-blue-900 shadow">
          <FaChartLine className="text-white text-2xl" />
        </div>
        <span className="text-xs text-gray-500 dark:text-gray-400 font-medium tracking-wider uppercase">Switch</span>
      </div>

      <div className="flex-1 flex flex-col gap-2 w-full">
        <label htmlFor="account-select-btn" className="block font-bold text-gray-800 dark:text-gray-100 mb-1 text-base">
          Select a Trading Account
        </label>

        {/* Button that shows current selection */}
        <button
          id="account-select-btn"
          ref={buttonRef}
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen(o => !o)}
          className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-white font-semibold flex items-center gap-2"
        >
          <span className="truncate font-semibold text-gray-900 dark:text-gray-100">{selected?.name}</span>
          {typePill(selected?.type)}
          {isBacktesting(selected) }
          {roleBadge(deriveCopyRole(selected))}
          <FaChevronDown className="ml-auto opacity-60" />
        </button>

        {/* Listbox */}
        {open && (
          <ul
            ref={listRef}
            role="listbox"
            aria-label="Accounts"
            className="mt-2 max-h-64 overflow-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg"

          >
            {sortedAccounts.map((acc, i) => {
              const active = i === activeIndex;
              const sel = acc.id === selected?.id;
              const role = deriveCopyRole(acc);
              return (
                <li
                  id={`acc-opt-${i}`}
                  key={acc.id}
                  role="option"
                  aria-selected={sel}
                  className={`px-3 py-2 cursor-pointer flex items-center gap-2 ${active ? "bg-blue-50 dark:bg-blue-900/30" : ""}`}
                  onMouseEnter={() => setActiveIndex(i)}
                  onClick={() => { onSelectAccount(acc.id); setOpen(false); }}
                  title={`${acc.name}${acc.type ? ` (${acc.type})` : ""}`}
                >
                  <span className="truncate font-semibold text-gray-900 dark:text-gray-100">{acc.name}</span>
                  {typePill(acc.type)}
                  {isBacktesting(acc)}
                  {roleBadge(role)}
                </li>
              );
            })}
          </ul>
        )}

        <div className="text-xs text-gray-400 dark:text-gray-500 mt-1 pl-1">
          Switch between accounts to view separate journals and stats.
        </div>
      </div>
    </div>
  );
}
